import { useCallback, useEffect, useRef, useState } from "react";

import { apiGet, apiPost } from "../../../Api/Client.js";
import { pollTimedOut } from "./dossierPolling.js";

function errorMessage(error, fallback = "Something Went Wrong.") {
  const payload = error?.payload;
  if (error?.status === 403) return payload?.error || payload?.detail || "You Don't Have Permission To Generate This Dossier.";
  if (payload?.error) return payload.error;
  if (payload?.detail) return payload.detail;
  if (payload && typeof payload === "object") {
    const firstKey = Object.keys(payload)[0];
    const value = payload[firstKey];
    if (Array.isArray(value)) return value.join(" ");
    if (typeof value === "string") return value;
  }
  return error?.message || fallback;
}

export default function useDossier(leadId) {
  const [dossiers, setDossiers] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeDossier, setActiveDossier] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingStatus, setGeneratingStatus] = useState("pending");
  const [generationError, setGenerationError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState("form");
  const pollRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      if (typeof pollRef.current === "object" && pollRef.current.timeoutId) {
        window.clearTimeout(pollRef.current.timeoutId);
      } else {
        window.clearInterval(pollRef.current);
        window.clearTimeout(pollRef.current);
      }
      pollRef.current = null;
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    if (!leadId) return;
    setLoadingHistory(true);
    try {
      const payload = await apiGet(`/Banao/leads/${leadId}/dossiers/`);
      setDossiers(Array.isArray(payload) ? payload : []);
    } catch (error) {
      setGenerationError(errorMessage(error, "Unable To Load Dossier History."));
    } finally {
      setLoadingHistory(false);
    }
  }, [leadId]);

  const fetchStatusOnce = useCallback(async (dossierId) => {
    return apiGet(`/Banao/leads/${leadId}/dossiers/${dossierId}/status/`);
  }, [leadId]);

  const pollStatus = useCallback((dossierId) => {
    stopPolling();
    setIsGenerating(true);
    setGenerationError(null);

    const startedAt = Date.now();
    const pollInstanceId = Math.random().toString(36).substring(7);
    pollRef.current = { id: pollInstanceId, timeoutId: null };

    const tick = async () => {
      if (!pollRef.current || pollRef.current.id !== pollInstanceId) return;

      try {
        const payload = await fetchStatusOnce(dossierId);
        if (!pollRef.current || pollRef.current.id !== pollInstanceId) return;

        const nextStatus = payload?.status || "pending";
        setGeneratingStatus(nextStatus);

        if (nextStatus === "complete") {
          setIsGenerating(false);
          setActiveDossier((current) => ({ ...(current || {}), ...payload, id: payload.dossier_id || dossierId }));
          setModalStep("viewer");
          fetchHistory();
          pollRef.current = null;
          return;
        }

        if (nextStatus === "failed" || nextStatus === "needs_input") {
          setIsGenerating(false);
          setGenerationError(payload?.error_message || "Dossier Generation Stopped.");
          setActiveDossier((current) => ({ ...(current || {}), ...payload, id: payload.dossier_id || dossierId }));
          pollRef.current = null;
          return;
        }

        if (pollTimedOut(startedAt)) {
          // Still not finished: stop asking and let the person leave; it stays in their history.
          setIsGenerating(false);
          setGeneratingStatus("stalled");
          pollRef.current = null;
          return;
        }

        if (pollRef.current.id === pollInstanceId) {
          pollRef.current.timeoutId = window.setTimeout(tick, 3000);
        }
      } catch (error) {
        if (!pollRef.current || pollRef.current.id !== pollInstanceId) return;
        setIsGenerating(false);
        setGenerationError(errorMessage(error, "Unable To Check Dossier Status."));
        pollRef.current = null;
      }
    };

    tick();
  }, [fetchHistory, fetchStatusOnce, stopPolling]);

  const startGeneration = useCallback(async (formData) => {
    if (!leadId) return;
    stopPolling();
    setGenerationError(null);
    setGeneratingStatus("pending");
    try {
      const payload = await apiPost(`/Banao/leads/${leadId}/dossiers/`, formData);
      setActiveDossier(payload);
      setModalStep("generating");
      pollStatus(payload.id || payload.dossier_id);
    } catch (error) {
      setGenerationError(errorMessage(error, "Unable To Start Dossier Generation."));
      throw error;
    }
  }, [leadId, pollStatus, stopPolling]);

  const viewDossier = useCallback(async (dossier) => {
    const dossierId = dossier?.id || dossier?.dossier_id;
    if (!dossierId) return;
    setGenerationError(null);
    try {
      const payload = await fetchStatusOnce(dossierId);
      setActiveDossier({ ...dossier, ...payload, id: dossierId });
      setGeneratingStatus(payload?.status || dossier.status);
      setModalStep(payload?.status === "complete" ? "viewer" : "generating");
      if (payload?.status === "pending" || payload?.status === "generating") {
        pollStatus(dossierId);
      }
      if (payload?.status === "failed" || payload?.status === "needs_input") {
        setGenerationError(payload?.error_message || "Dossier Is Not Complete.");
      }
    } catch (error) {
      setGenerationError(errorMessage(error, "Unable To Load Dossier."));
    }
  }, [fetchStatusOnce, pollStatus]);

  const downloadDossier = useCallback(async (dossierId, prospectName = "prospect") => {
    try {
      const payload = await apiGet(`/Banao/leads/${leadId}/dossiers/${dossierId}/download/`);
      const text = typeof payload === "string" ? payload : payload?.raw || "";
      const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dossier_${String(prospectName || "prospect").replace(/\s+/g, "_")}_${dossierId}.md`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setGenerationError(errorMessage(error, "Unable To Download Dossier."));
    }
  }, [leadId]);

  const openModal = useCallback(() => {
    setModalOpen(true);
    setModalStep("form");
    setGenerationError(null);
    fetchHistory();
  }, [fetchHistory]);

  const closeModal = useCallback(() => {
    stopPolling();
    setModalOpen(false);
    setModalStep("form");
    setGenerationError(null);
    setIsGenerating(false);
  }, [stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [leadId, stopPolling]);

  return {
    dossiers,
    loadingHistory,
    fetchHistory,
    activeDossier,
    setActiveDossier,
    isGenerating,
    generatingStatus,
    generationError,
    startGeneration,
    pollStatus,
    downloadDossier,
    viewDossier,
    modalOpen,
    openModal,
    closeModal,
    modalStep,
    setModalStep,
  };
}
